import type { QueryClient } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, resolveCharIdFromJwt } from '@/lib/axios';

export interface AccountSettings {
  id: number;
  jid: number;
  language: string;
  timezone: string | null;
  country: string | null;
  whatsapp_phone: string | null;
  whatsapp_verified: number;
  whatsapp_verified_at: string | null;
}

export interface EventNotificationRow {
  id: number;
  jid: number;
  notification_type: string;
  notification_key: string;
  notification_name: string;
  is_enabled: number;
  reminder_minutes: number | null;
}

export interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  total_seconds: number;
  expired: boolean;
  expires_at: string | null;
  validated_at?: string | null;
}

export interface SettingsState {
  status: 'success';
  accountSettings: AccountSettings;
  eventNotifications: EventNotificationRow[];
  validationStatus: {
    needs_validation: boolean;
    time_remaining: TimeRemaining;
    should_notify: boolean;
  } | null;
  timeRemaining: TimeRemaining | null;
  hwid: string;
  languages: Record<string, string>;
  timezones: Record<string, string>;
  countries: Record<string, string>;
  countryCodes: Record<string, string>;
}

export interface SettingsMutationResult {
  success: boolean;
  message?: string;
  language?: string | null;
  verified?: boolean;
  time_remaining?: TimeRemaining;
}

function settingsQueryKey(charId: number): (string | number)[] {
  return ['settings', charId];
}

async function fetchSettings(): Promise<SettingsState> {
  const { data } = await api.get<SettingsState>('/api/v1/settings');
  return data;
}

export function useSettings() {
  const charId = resolveCharIdFromJwt();
  return useQuery({
    queryKey: settingsQueryKey(charId),
    queryFn: fetchSettings,
    enabled: charId > 0,
    staleTime: 30_000,
  });
}

export function useUpdateSettingsGeneral() {
  const queryClient = useQueryClient();
  const charId = resolveCharIdFromJwt();

  return useMutation({
    mutationFn: async (payload: { language: string; timezone: string; country: string }) => {
      const { data } = await api.patch<SettingsMutationResult>('/api/v1/settings/general', payload);
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsQueryKey(charId) });
    },
  });
}

export function useUpdateNotification() {
  return useMutation({
    mutationFn: async (payload: {
      eventName: string;
      enabled: boolean;
      reminderMinutes: number | null;
    }) => {
      const encoded = encodeURIComponent(payload.eventName);
      const { data } = await api.patch<SettingsMutationResult>(
        `/api/v1/settings/notifications/${encoded}`,
        {
          enabled: payload.enabled,
          reminder_minutes: payload.reminderMinutes,
        },
      );
      return data;
    },
  });
}

export function prefetchSettings(queryClient: QueryClient, charId: number): void {
  if (!charId) return;
  void queryClient.prefetchQuery({
    queryKey: settingsQueryKey(charId),
    queryFn: fetchSettings,
    staleTime: 30_000,
  });
}
